"use client";

import { useTestimonials } from "@/hooks/usePublicData";
import type { TestimonialData } from "@/services/public.service";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { useRef } from "react";
import type { Swiper as SwiperType } from "swiper";
import { A11y, Autoplay, Keyboard, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import "swiper/css";
import "swiper/css/pagination";

const API_URL = process.env.NEXT_PUBLIC_API_URI ?? "";
const resolve = (src?: string) =>
  !src ? "" : src.startsWith("http") || src.startsWith("/") ? src : `${API_URL}/${src.replace(/^\//, "")}`;

function Card({ item, isAr }: { item: TestimonialData; isAr: boolean }) {
  const name = (isAr ? item.name.ar : item.name.en) || item.name.ar || item.name.en;
  const subtitle = (isAr ? item.subtitle.ar : item.subtitle.en) || item.subtitle.ar || item.subtitle.en;
  const text = (isAr ? item.text.ar : item.text.en) || item.text.ar || item.text.en;
  const avatar = resolve(item.avatar);

  return (
    <figure className="group relative flex h-full flex-col rounded-3xl border border-clay-100 bg-surface p-6 md:p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-18px_rgba(28,25,23,0.25)]">
      <Quote
        aria-hidden
        className="absolute end-6 top-6 size-10 text-clay-600/10 transition-colors group-hover:text-clay-600/20"
        strokeWidth={1.5}
      />

      <div className="mb-4 flex gap-0.5" dir="ltr" aria-label={`${item.rating}/5`}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            className={`size-4 ${i <= item.rating ? "fill-gold-500 text-gold-500" : "fill-transparent text-clay-100"}`}
          />
        ))}
      </div>

      <blockquote className="flex-1 text-[15px] leading-8 text-ink-900/85 line-clamp-6">“{text}”</blockquote>

      <figcaption className="mt-6 flex items-center gap-3 border-t border-clay-100 pt-5">
        {avatar ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={avatar} alt={name} loading="lazy" className="size-12 rounded-full object-cover ring-2 ring-clay-100" />
        ) : (
          <span className="flex size-12 items-center justify-center rounded-full bg-clay-600/10 font-display text-lg font-bold text-clay-600">
            {name?.trim().charAt(0) || "?"}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate font-bold text-ink-900">{name}</p>
          {subtitle && <p className="truncate text-xs text-ink-500">{subtitle}</p>}
        </div>
      </figcaption>
    </figure>
  );
}

export default function Testimonials({ isAr }: { isAr: boolean }) {
  const t = (a: string, e: string) => (isAr ? a : e);
  const { data: items = [], isLoading } = useTestimonials();
  const swiperRef = useRef<SwiperType | null>(null);

  // Nothing to show → no empty section on the home page.
  if (!isLoading && items.length === 0) return null;

  const loop = items.length > 3;
  const PrevIcon = isAr ? ChevronRight : ChevronLeft;
  const NextIcon = isAr ? ChevronLeft : ChevronRight;
  const navBtn =
    "flex size-11 items-center justify-center rounded-full border border-clay-100 bg-surface text-ink-900 transition-all hover:border-clay-600 hover:bg-clay-600 hover:text-white disabled:opacity-40";

  return (
    <section className="relative overflow-hidden bg-clay-50/60 py-16 md:py-24" aria-labelledby="testimonials-title">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="mb-10 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div className="max-w-xl">
            <p className="mb-2 text-sm font-bold tracking-wide text-clay-600">{t("آراء العملاء", "Testimonials")}</p>
            <h2 id="testimonials-title" className="font-display text-3xl font-bold text-ink-900 md:text-4xl">
              {t("ماذا يقول ضيوفنا", "What our guests say")}
            </h2>
            <p className="mt-3 text-ink-500">
              {t("تجارب حقيقية من ضيوف أقاموا في منازل الدرعية.", "Real stories from guests who stayed with Diriyah Homes.")}
            </p>
          </div>
          {items.length > 1 && (
            <div className="flex gap-2">
              <button type="button" aria-label={t("السابق", "Previous")} onClick={() => swiperRef.current?.slidePrev()} className={navBtn}>
                <PrevIcon className="size-5" />
              </button>
              <button type="button" aria-label={t("التالي", "Next")} onClick={() => swiperRef.current?.slideNext()} className={navBtn}>
                <NextIcon className="size-5" />
              </button>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={`h-72 animate-pulse rounded-3xl bg-clay-100/50 ${i > 0 ? "hidden md:block" : ""} ${i > 1 ? "md:hidden lg:block" : ""}`} />
            ))}
          </div>
        ) : (
          <div dir={isAr ? "rtl" : "ltr"}>
            <Swiper
              key={isAr ? "rtl" : "ltr"}
              modules={[Autoplay, Pagination, Keyboard, A11y]}
              onSwiper={(s) => (swiperRef.current = s)}
              spaceBetween={20}
              slidesPerView={1.08}
              breakpoints={{
                640: { slidesPerView: 1.6, spaceBetween: 20 },
                768: { slidesPerView: 2, spaceBetween: 24 },
                1024: { slidesPerView: 3, spaceBetween: 24 },
              }}
              loop={loop}
              autoplay={items.length > 1 ? { delay: 5500, disableOnInteraction: false, pauseOnMouseEnter: true } : false}
              keyboard={{ enabled: true, onlyInViewport: true }}
              pagination={{ clickable: true, el: ".dh-testimonials-pagination" }}
              a11y={{
                prevSlideMessage: t("الرأي السابق", "Previous testimonial"),
                nextSlideMessage: t("الرأي التالي", "Next testimonial"),
              }}
              className="!overflow-visible !pb-2"
            >
              {items.map((item) => (
                <SwiperSlide key={item._id} className="!h-auto">
                  <Card item={item} isAr={isAr} />
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="dh-testimonials-pagination mt-8 flex justify-center gap-2" />
          </div>
        )}
      </div>
    </section>
  );
}
